import ArtifactStatusChip from "@/components/evidenceGathering/ArtifactStatusChip";
import { normalizeArtifactType } from "@/components/evidenceGathering/ArtifactTypeBadge";
import ArtifactVersionBadge from "@/components/evidenceGathering/ArtifactVersionBadge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useArtifactFile } from "@/hooks/useArtifactFile";
import { isFromLatestUpload, isTableArtifact } from "@/lib/artifacts";
import { parseCsv } from "@/lib/csv";
import type { Artifact } from "@/services/projectsApi";
import { Card } from "@lumanity/ui/primitives";
import { FileText, ImageOff, Loader2, MoreHorizontal } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

interface AddedArtifactCardProps {
  artifact: Artifact;
  projectId: string;
  onView: () => void;
  /** The project's highest upload version, for deciding whether this artefact
   *  came from the latest upload. */
  latestUploadVersion?: number;
  onRemove?: () => void;
  isRemoving?: boolean;
  selected?: boolean;
  onToggleSelect?: () => void;
}

const formatDate = (iso: string | null) => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const AddedArtifactCard = ({
  artifact,
  projectId,
  onView,
  onRemove,
  isRemoving = false,
  selected = false,
  onToggleSelect,
  latestUploadVersion = 0,
}: AddedArtifactCardProps) => {
  const selectable = !!onToggleSelect;
  const kind = normalizeArtifactType(artifact.type);
  const isPicture = kind === "Image" || kind === "Chart";
  const isTable = isTableArtifact(artifact);
  const { blobUrl, text, isLoading, error, fetchFile, clearFile } = useArtifactFile();

  // Opening a dialog straight from a menu item puts two Radix layers on top of
  // each other mid-transition. The dialog marks everything outside itself
  // aria-hidden while the menu still holds focus, and both layers manage
  // document.body's pointer-events, so their cleanups interleave and the body
  // can be left unclickable — after which the trigger does nothing, on every
  // card, until the page is reloaded.
  //
  // So the action waits for the menu to actually close before it runs.
  const [menuOpen, setMenuOpen] = useState(false);
  const pendingAction = useRef<(() => void) | null>(null);

  const runAfterMenuCloses = (action: () => void) => {
    pendingAction.current = action;
  };

  const handleMenuOpenChange = (open: boolean) => {
    setMenuOpen(open);
    if (open || !pendingAction.current) return;
    const action = pendingAction.current;
    pendingAction.current = null;
    // Next frame: Radix returns focus to the trigger and tears the menu layer
    // down as part of closing, and that has to finish first.
    requestAnimationFrame(action);
  };

  // A table's thumbnail is drawn from its CSV rather than a stored image: the
  // workbook holds no picture of a table, and the CSV is a few hundred bytes
  // against tens of kilobytes for a chart.
  //
  // updatedAt is read but deliberately not a dependency. It only versions the
  // URL so a re-extracted file isn't served from cache; depending on it would
  // re-download every thumbnail on each enrichment status change, since those
  // bump the row's timestamp too.
  useEffect(() => {
    if (artifact.contentType) fetchFile(projectId, artifact.uid, isTable, artifact.updatedAt);
    return clearFile;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artifact.contentType, isTable, artifact.uid, projectId, fetchFile, clearFile]);

  // Only what fits legibly in the tile — enough to recognise the table by.
  const preview = useMemo(() => {
    if (!text) return [];
    // Parsed properly rather than split on delimiters: a quoted cell containing
    // a comma would otherwise become several cells and shift every column
    // after it.
    return parseCsv(text)
      .slice(0, 5)
      .map((row) => row.slice(0, 4));
  }, [text]);

  const updated = formatDate(artifact.updatedAt || artifact.createdAt);
  const hasPreview = isPicture && !!blobUrl && !error;

  return (
    <Card
      className={`relative w-[168px] shrink-0 rounded-md overflow-hidden transition-colors hover:border-brand-teal/50 ${selected ? "border-brand-teal ring-1 ring-brand-teal" : "border-border"
        }`}
    >
      {selectable && (
        <div className="absolute top-1.5 left-1.5 z-10" onClick={(e) => e.stopPropagation()}>
          <Checkbox
            checked={selected}
            onCheckedChange={onToggleSelect}
            aria-label={`Select ${artifact.name}`}
            className="bg-card"
          />
        </div>
      )}

      <div
        onClick={onToggleSelect ?? onView}
        className={`cursor-pointer ${hasPreview || preview.length > 0 ? "" : "bg-muted/40"}`}
      >
        <div className="h-[104px] flex items-center justify-center p-2">
          {isLoading && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
          {hasPreview && (
            <img src={blobUrl!} alt="" className="max-w-full max-h-full object-contain" />
          )}
          {isPicture && !isLoading && !hasPreview && (
            <ImageOff className="w-5 h-5 text-muted-foreground/60" />
          )}
          {isTable && preview.length > 0 && (
            <div className="w-full h-full overflow-hidden">
              <table className="w-full border-collapse text-[5px] leading-[1.5] table-fixed">
                <tbody>
                  {preview.map((row, r) => (
                    <tr key={r}>
                      {row.map((cell, c) => (
                        <td key={c} className="border border-border px-[2px] truncate text-foreground">
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {isTable && preview.length === 0 && !isLoading && (
            <div className="w-9 h-11 rounded border bg-card flex items-center justify-center">
              <FileText className="w-4 h-4 text-muted-foreground/70" />
            </div>
          )}
          {!isPicture && !isTable && (
            <div className="w-9 h-11 rounded border bg-card flex items-center justify-center">
              <FileText className="w-4 h-4 text-muted-foreground/70" />
            </div>
          )}
        </div>

        {updated && (
          <p className="px-2 pb-1.5 text-[10px] text-muted-foreground text-center truncate">
            Last updated: {updated}
          </p>
        )}
      </div>

      {/* Stops the row action from opening the preview the tile opens. */}
      <div className="absolute top-1 right-1" onClick={(e) => e.stopPropagation()}>
        {/* Not modal: a modal menu locks body scroll and pointer-events, which
            is the half of the clash above that outlives the menu itself. */}
        <DropdownMenu open={menuOpen} onOpenChange={handleMenuOpenChange} modal={false}>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="w-6 h-6 text-muted-foreground hover:bg-muted"
              disabled={isRemoving}
            >
              {isRemoving ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <MoreHorizontal className="w-4 h-4" />
              )}
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onSelect={() => runAfterMenuCloses(onView)}>Preview</DropdownMenuItem>
            {onRemove && (
              <DropdownMenuItem
                onSelect={() => runAfterMenuCloses(onRemove)}
                className="text-destructive focus:text-destructive"
              >
                Remove from project
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="border-t border-border px-2 pb-2">
        <ArtifactStatusChip artifact={artifact} className="mt-1.5 text-[11px]" hideRoutine />
        <p className="mt-1.5 text-[11px] text-muted-foreground truncate" title={artifact.uid}>
          {artifact.uid}
        </p>
        <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
          {kind && <span className="truncate">{kind} · Upload</span>}
          <ArtifactVersionBadge
            uploadVersion={artifact.uploadVersion}
            highlight={isFromLatestUpload(artifact, latestUploadVersion)}
          />
        </div>
        <button
          type="button"
          onClick={onView}
          className="text-left text-[13px] font-medium text-brand-teal hover:underline line-clamp-2"
        >
          {artifact.name}
        </button>
      </div>
    </Card>
  );
};

export default AddedArtifactCard;
